import { PlusOutlined } from '@ant-design/icons'

import { createNewsApi } from '@eastcoal/api/content/newsOperation/createNews'
import { queryNewsApi } from '@eastcoal/api/content/newsOperation/queryNews'
import { updateNewsApi } from '@eastcoal/api/content/newsOperation/updateNews'

import { ObjectAcl } from '@eastcoal/common/enums/ObjectAcl'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { useUrlParams } from '@eastcoal/common/hooks/useUrlParams'
import { mapNumberEnumOptions } from '@eastcoal/common/utils/enums'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { SubmitType } from '@eastcoal/formily/components/CrudForm/enums/SubmitType'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { RadioGroup } from '@eastcoal/formily/components/Radio'
import { RichEditor } from '@eastcoal/formily/components/RichEditor'
import { TextArea } from '@eastcoal/formily/components/TextArea'
import { UploadFile } from '@eastcoal/formily/components/UploadFile'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'
import { NewsCatalogueCode } from '@eastcoal/shared/enums/NewsCatalogueCode'
import { NewsRecommendMethodTextMap } from '@eastcoal/shared/enums/NewsRecommendMethod'

import { NewsCatalogueCascader } from '../components/NewsCatalogueCascader'

import { NewsFormFormilyProps, NewsFormProps } from './interface'

const schema: NewsFormFormilyProps['schema'] = [
  {
    name: 'title',
    type: 'string',
    title: '文章标题',
    required: true,
    'x-component': Input,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 480
    }
  },
  {
    name: '{ id: catalogueId, code: catalogueCode }',
    type: 'object',
    title: '所属目录',
    'x-component': NewsCatalogueCascader,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 480
    }
  },
  {
    name: '{ id: kindId, name: kindName, code: kindCode }',
    type: 'object',
    title: '品种',
    'x-display':
      '{{ $values.catalogueCode === $context.EXCLUSIVE_ANALYSIS_CODE ? "visible" : "none" }}',
    'x-component': KindAllNodeCascader,
    'x-decorator': FormItem,
    'x-decorator-props': {
      wrapperWidth: 480
    }
  },
  {
    name: 'recommendMethod',
    type: 'string',
    title: '推荐方式',
    enum: mapNumberEnumOptions(NewsRecommendMethodTextMap),
    default: 0,
    'x-component': RadioGroup,
    'x-decorator': FormItem
  },
  {
    name: 'ossIds',
    type: 'array',
    title: '略缩图',
    required: true,
    'x-display':
      '{{ $values.recommendMethod === 1 || $values.recommendMethod === 2 ? "visible" : "none" }}',
    'x-component': UploadFile,
    'x-component-props': {
      objectAcl: ObjectAcl.PUBLIC_READ,
      maxCount: 1,
      accept: 'image/*',
      listType: 'picture-card',
      children: <PlusOutlined />
    },
    'x-decorator': FormItem
  },
  {
    name: 'description',
    type: 'string',
    title: '文章摘要',
    required: '{{$values.recommendMethod != 0}}',
    'x-component': TextArea,
    'x-decorator': FormItem
  },
  {
    name: 'content',
    type: 'string',
    title: '文章内容',
    'x-component': RichEditor,
    'x-decorator': FormItem
  }
]

const readApi: NewsFormFormilyProps['readApi'] = ({ id }) => {
  return {
    ready: !!id,
    refreshDeps: [id],
    api: async () => {
      const data = await queryNewsApi({ id })
      return {
        ...data,
        ossIds: data.imageOssId ? [data.imageOssId] : undefined
      }
    }
  }
}

const createApi: NewsFormFormilyProps['createApi'] = (
  { ossIds, ...restData },
  { submitType }
) => {
  return createNewsApi({
    ...restData,
    imageOssId: ossIds?.[0],
    status: submitType === SubmitType.SUBMIT ? 1 : 0
  })
}

const updateApi: NewsFormFormilyProps['updateApi'] = (
  { ossIds, ...restData },
  { submitType }
) => {
  return updateNewsApi({
    ...restData,
    imageOssId: ossIds?.[0],
    status: submitType === SubmitType.SUBMIT ? 1 : 0
  })
}

const actions: NewsFormFormilyProps['actions'] = [
  <BackButton key='backButton' />,
  <SaveButton key='saveButton' />,
  <SubmitButton key='publishButton'>发布</SubmitButton>
]

export const NewsForm = ({ initialValues, ...restProps }: NewsFormProps) => {
  const { catalogueId, catalogueCode } = useUrlParams()

  const navigate = useNavigate()
  const { message } = useFeedback()

  const handleSubmitSuccess = () => {
    message.success('操作成功')
    navigate(-1)
  }

  const mergedInitialValues = initialValues ?? {
    catalogueId,
    catalogueCode
  }

  return (
    <CrudForm
      {...restProps}
      actions={actions}
      createApi={createApi}
      initialValues={mergedInitialValues}
      name='newsForm'
      readApi={readApi}
      schema={schema}
      updateApi={updateApi}
      context={{
        EXCLUSIVE_ANALYSIS_CODE: NewsCatalogueCode.EXCLUSIVE_ANALYSIS
      }}
      onSuccess={handleSubmitSuccess}
    />
  )
}
